import { mdiMicrophone, mdiPause, mdiPlay } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, IconButton, styled, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { extractFilenameFromUrl } from '@utils/fileUtils';

// Constants
const MEDIA_SIZES = {
	image: { width: 280, height: 200 },
	video: { width: 280, height: 200 },
	audio: { width: 250, height: 60 },
} as const;

// Utility functions
const formatDuration = (seconds: number): string => {
	if (isNaN(seconds) || !isFinite(seconds) || seconds < 0) {
		return '0:00';
	}
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);
	return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Audio waveform analysis utilities
const analyzeAudioWaveform = async (audioUrl: string): Promise<number[]> => {
	try {
		const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
		const response = await fetch(audioUrl);
		const arrayBuffer = await response.arrayBuffer();
		const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

		const channelData = audioBuffer.getChannelData(0); // Use first channel
		const sampleSize = Math.floor(channelData.length / 40); // 40 bars for waveform
		const waveformData: number[] = [];

		for (let i = 0; i < 40; i++) {
			const start = i * sampleSize;
			const end = Math.min(start + sampleSize, channelData.length);

			// Calculate RMS (Root Mean Square) for this segment
			let sum = 0;
			for (let j = start; j < end; j++) {
				sum += channelData[j] * channelData[j];
			}
			const rms = Math.sqrt(sum / (end - start));

			// Normalize to 0-1 range and apply some scaling
			const normalized = Math.min(rms * 10, 1);
			waveformData.push(normalized);
		}

		audioContext.close();
		return waveformData;
	} catch (error) {
		console.error('Error analyzing audio waveform:', error);
		// Return fallback sine wave pattern
		return Array.from({ length: 40 }, (_, i) => (Math.sin(i * 0.3) + 1) / 2);
	}
};

// Hook for waveform data
const useWaveformData = (src: string, staticWaveform?: number[]) => {
	const [waveformData, setWaveformData] = useState<number[]>(
		staticWaveform || Array.from({ length: 40 }, (_, i) => (Math.sin(i * 0.3) + 1) / 2)
	);
	const [isAnalyzing, setIsAnalyzing] = useState(!staticWaveform);

	useEffect(() => {
		if (staticWaveform) {
			setWaveformData(staticWaveform);
			setIsAnalyzing(false);
			return;
		}

		let isCancelled = false;

		const analyzeAudio = async () => {
			setIsAnalyzing(true);
			try {
				const data = await analyzeAudioWaveform(src);
				if (!isCancelled) {
					setWaveformData(data);
				}
			} catch (error) {
				console.error('Waveform analysis failed:', error);
			} finally {
				if (!isCancelled) {
					setIsAnalyzing(false);
				}
			}
		};

		analyzeAudio();

		return () => {
			isCancelled = true;
		};
	}, [src, staticWaveform]);

	return { waveformData, isAnalyzing };
};

// Styled components
const MediaContainer = styled(Box)({
	position: 'relative',
	maxWidth: '100%',
	borderRadius: '8px',
	overflow: 'hidden',
	backgroundColor: '#f0f0f0',
	cursor: 'pointer',
	'&:hover .media-overlay': {
		opacity: 1,
	},
});

const MediaOverlay = styled(Box)({
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	backgroundColor: 'rgba(0, 0, 0, 0.3)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	opacity: 0,
	transition: 'opacity 0.2s ease-in-out',
	borderRadius: '8px',
});

// Loading indicator component
const LoadingIndicator = () => (
	<Typography
		variant="caption"
		color="text.secondary"
		sx={{
			display: 'flex',
			alignItems: 'center',
			gap: 1,
			'&::before': {
				content: '""',
				display: 'block',
				width: '12px',
				height: '12px',
				borderRadius: '50%',
				border: '2px solid rgba(0,0,0,0.1)',
				borderTopColor: 'rgba(0,0,0,0.3)',
				animation: 'spin 1s linear infinite',
			},
			'@keyframes spin': {
				'0%': { transform: 'rotate(0deg)' },
				'100%': { transform: 'rotate(360deg)' },
			},
		}}
	>
		Loading...
	</Typography>
);

// Media loading hook
const useMediaLoading = (url: string) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const handleLoad = useCallback(() => {
		setLoading(false);
		setError(false);
	}, []);

	const handleError = useCallback(() => {
		setLoading(false);
		setError(true);
	}, []);

	// Timeout fallback
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (loading) {
				setLoading(false);
			}
		}, 2500);

		return () => clearTimeout(timeoutId);
	}, [loading, url]);

	return { loading, error, handleLoad, handleError };
};

// Media placeholder component
export const MediaPlaceholder: React.FC<{
	width: number;
	height: number;
	loading: boolean;
	error: boolean;
	errorMessage?: string;
}> = ({ width, height, loading, error, errorMessage = 'Failed to load media' }) => {
	if (loading) {
		return (
			<Box
				sx={{
					width,
					height,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: '#f5f5f5',
					borderRadius: '8px',
				}}
			>
				<LoadingIndicator />
			</Box>
		);
	}

	if (error) {
		return (
			<Box
				sx={{
					width,
					height,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: '#ffeaea',
					borderRadius: '8px',
					color: '#d32f2f',
					p: 2,
				}}
			>
				<Typography variant="caption" align="center">
					{errorMessage}
				</Typography>
			</Box>
		);
	}

	return null;
};

// Enhanced image component with better loading states
export const MediaImage: React.FC<{
	src: string;
	alt: string;
	width?: number;
	height?: number;
	onClick?: () => void;
	fileName?: string;
	dimensions?: { width: number; height: number };
}> = ({
	src,
	alt,
	width = MEDIA_SIZES.image.width,
	height = MEDIA_SIZES.image.height,
	onClick,
	fileName,
	dimensions,
}) => {
	const { loading, error, handleLoad, handleError } = useMediaLoading(src);

	// Get display filename - use fileName if available, otherwise extract from URL
	const displayFileName = fileName || extractFilenameFromUrl(src);

	return (
		<MediaContainer sx={{ width, height: 'auto' }} onClick={onClick}>
			<MediaPlaceholder
				width={width}
				height={height}
				loading={loading}
				error={error}
				errorMessage="Failed to load image"
			/>

			{!error && (
				<>
					<img
						src={src}
						alt={alt}
						width={width}
						height={height}
						loading="lazy"
						style={{
							width,
							height,
							display: loading ? 'none' : 'block',
							objectFit: 'cover',
							borderRadius: '8px',
						}}
						onLoad={handleLoad}
						onError={handleError}
					/>

					{!loading && onClick && (
						<MediaOverlay className="media-overlay">
							<Box
								sx={{
									color: 'white',
									bgcolor: 'rgba(0,0,0,0.4)',
									px: 2,
									py: 1,
									borderRadius: 1,
									fontSize: '12px',
									fontWeight: 600,
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
							>
								View Full Size
							</Box>
						</MediaOverlay>
					)}
				</>
			)}

			{displayFileName && (
				<Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', px: 1 }}>
					{displayFileName}
					{dimensions && ` (${dimensions.width}×${dimensions.height})`}
				</Typography>
			)}
		</MediaContainer>
	);
};

// Enhanced video component
export const MediaVideo: React.FC<{
	src: string;
	previewSrc?: string | null;
	width?: number;
	height?: number;
	onClick?: () => void;
	fileName?: string;
	duration?: number;
}> = ({
	src,
	previewSrc,
	width = MEDIA_SIZES.video.width,
	height = MEDIA_SIZES.video.height,
	onClick,
	fileName,
	duration,
}) => {
	const { loading, error, handleLoad, handleError } = useMediaLoading(previewSrc || src);

	// Get display filename - use fileName if available, otherwise extract from URL
	const displayFileName = fileName || extractFilenameFromUrl(src);

	return (
		<MediaContainer sx={{ width, height: 'auto' }} onClick={onClick}>
			<MediaPlaceholder
				width={width}
				height={height}
				loading={loading}
				error={error}
				errorMessage="Failed to load video"
			/>

			{!error && (
				<>
					{previewSrc ? (
						<img
							src={previewSrc}
							alt="video thumbnail"
							width={width}
							height={height}
							loading="lazy"
							style={{
								width,
								height,
								display: loading ? 'none' : 'block',
								objectFit: 'cover',
								borderRadius: '8px',
							}}
							onLoad={handleLoad}
							onError={handleError}
						/>
					) : (
						<video
							src={src}
							style={{
								width,
								height,
								display: loading ? 'none' : 'block',
								objectFit: 'cover',
								borderRadius: '8px',
							}}
							onLoadedMetadata={handleLoad}
							onError={handleError}
						/>
					)}

					{!loading && (
						<MediaOverlay className="media-overlay">
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: 60,
									height: 60,
									borderRadius: '50%',
									bgcolor: 'rgba(0,0,0,0.7)',
									color: 'white',
									transition: 'all 0.2s ease',
									'&:hover': {
										bgcolor: 'rgba(0,0,0,0.8)',
										transform: 'scale(1.1)',
									},
								}}
							>
								<Icon path={mdiPlay} size={1.5} />
							</Box>
						</MediaOverlay>
					)}
				</>
			)}

			{displayFileName && (
				<Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666', px: 1 }}>
					{displayFileName}
					{duration && ` · ${formatDuration(duration)}`}
				</Typography>
			)}
		</MediaContainer>
	);
};

// Enhanced voice message component
export const VoiceMessage: React.FC<{
	src: string;
	isCurrentUser: boolean;
	fileName?: string;
	waveformData?: number[]; // Optional pre-computed waveform from backend
}> = ({ src, isCurrentUser, fileName, waveformData }) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const [currentTime, setCurrentTime] = useState('0:00');
	const [duration, setDuration] = useState('0:00');
	const [loadError, setLoadError] = useState(false);
	const audioRef = useRef<HTMLAudioElement>(null);

	// Use dynamic waveform analysis
	const { waveformData: dynamicWaveform, isAnalyzing } = useWaveformData(src, waveformData);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const updateProgress = () => {
			if (audio && !isNaN(audio.duration) && !isNaN(audio.currentTime)) {
				const progressValue = (audio.currentTime / audio.duration) * 100;
				setProgress(isNaN(progressValue) ? 0 : progressValue);
				setCurrentTime(formatDuration(audio.currentTime));
			}
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setProgress(0);
			if (audio) audio.currentTime = 0;
		};

		const handleLoadedMetadata = () => {
			if (audio && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
				setDuration(formatDuration(audio.duration));
				setLoadError(false);
			} else {
				// Fallback for problematic audio files
				setTimeout(() => {
					if (audio && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
						setDuration(formatDuration(audio.duration));
						setLoadError(false);
					} else {
						setDuration('0:30'); // Default fallback duration
						setLoadError(false);
					}
				}, 1000);
			}
		};

		const handleError = () => setLoadError(true);

		audio.addEventListener('timeupdate', updateProgress);
		audio.addEventListener('ended', handleEnded);
		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('error', handleError);

		return () => {
			audio.removeEventListener('timeupdate', updateProgress);
			audio.removeEventListener('ended', handleEnded);
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
			audio.removeEventListener('error', handleError);
		};
	}, [src]);

	const togglePlayback = useCallback(() => {
		if (audioRef.current && !loadError) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play().catch((error) => {
					console.error('Failed to play audio:', error);
					setLoadError(true);
				});
			}
			setIsPlaying(!isPlaying);
		}
	}, [isPlaying, loadError]);

	if (loadError) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: '#d32f2f',
					backgroundColor: '#ffeaea',
					borderRadius: '12px',
					p: 2,
					minWidth: '200px',
				}}
			>
				<Typography variant="caption">Failed to load voice message</Typography>
			</Box>
		);
	}

	const primaryColor = isCurrentUser ? '#25D366' : '#128C7E';
	const hoverColor = isCurrentUser ? '#1ea952' : '#0E6C60';
	const backgroundColor = isCurrentUser ? 'rgba(37, 211, 102, 0.1)' : '#f0f2f5';

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1.5,
				padding: '10px 14px',
				backgroundColor,
				borderRadius: '16px',
				minWidth: '220px',
				maxWidth: '320px',
				border: isCurrentUser ? `1px solid rgba(37, 211, 102, 0.2)` : 'none',
			}}
		>
			<audio ref={audioRef} src={src} preload="metadata" />

			{/* Enhanced play/pause button */}
			<IconButton
				size="small"
				onClick={togglePlayback}
				sx={{
					backgroundColor: primaryColor,
					color: 'white',
					width: 36,
					height: 36,
					transition: 'all 0.2s ease',
					'&:hover': {
						backgroundColor: hoverColor,
						transform: 'scale(1.05)',
					},
				}}
			>
				<Icon path={isPlaying ? mdiPause : mdiPlay} size={0.7} />
			</IconButton>

			{/* Enhanced waveform and controls */}
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
				{/* Dynamic waveform visualization */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: { xs: 0.1, sm: 0.3 },
						height: '24px',
						position: 'relative',
					}}
				>
					{isAnalyzing && (
						<Box
							sx={{
								position: 'absolute',
								left: 0,
								right: 0,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: '#666',
								fontSize: '0.7rem',
							}}
						>
							Analyzing...
						</Box>
					)}
					{dynamicWaveform.map((amplitude, i) => {
						const height = Math.max(4, amplitude * 20 + 4); // Scale amplitude to visual height
						const isActive = i < (progress / 100) * dynamicWaveform.length;
						return (
							<Box
								key={i}
								sx={{
									width: '3px',
									height: `${height}px`,
									backgroundColor: isActive ? primaryColor : 'rgba(0,0,0,0.2)',
									borderRadius: '2px',
									transition: 'all 0.2s ease',
									transform: isActive ? 'scaleY(1.1)' : 'scaleY(1)',
									opacity: isAnalyzing ? 0.3 : 1,
								}}
							/>
						);
					})}
				</Box>

				{/* Enhanced time display */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography
						variant="caption"
						sx={{
							color: '#666',
							fontSize: '0.75rem',
							fontWeight: 500,
						}}
					>
						{isPlaying ? `${currentTime} / ${duration}` : duration}
					</Typography>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Icon path={mdiMicrophone} size={0.6} color={primaryColor} />
						<Typography
							variant="caption"
							sx={{
								color: '#666',
								fontSize: '0.65rem',
								fontStyle: 'italic',
							}}
						>
							Voice
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export { MEDIA_SIZES };
